import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Meta } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly titleService: Title = inject(Title);
  private metaService: Meta = inject(Meta);

  /**
   * Updates the document's title using the provided string.
   *
   * @param title - The new title to set for the document.
   */
  updateTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  /**
   * Updates multiple meta tags in the document head.
   *
   * Iterates over the provided array of meta tag objects and updates each tag using the meta service.
   *
   * @param metaTags - An array of objects, each containing a `name` and `content` property, representing the meta tags to update.
   */
  updateMetaTags(metaTags: Record<string, string>[]): void {
    metaTags.forEach((tag) => {
      this.metaService.updateTag(tag);
    });
  }

  /**
   * Updates the SEO metadata for the current page.
   *
   * This method sets the page title and updates meta tags for description and keywords
   * based on the provided `seo` object.
   *
   * @param seo - An object containing optional SEO metadata:
   *   - `title` (optional): The new title for the page.
   *   - `description` (optional): The meta description content.
   *   - `keywords` (optional): The meta keywords content.
   */
  updateSeoData(seo: { title?: string; description?: string; keywords?: string }): void {
    if (seo.title) {
      this.updateTitle(seo.title);
    }

    const metaList: { name: string; content: string }[] = [];

    if (seo.description) {
      metaList.push({ name: 'description', content: seo.description });
    }

    if (seo.keywords) {
      metaList.push({ name: 'keywords', content: seo.keywords });
    }

    this.updateMetaTags(metaList);
  }
}
